/**
 * AskTillyModal
 *
 * Post-onboarding entry point to the concierge (see RFDs/rfd-7-ai-concierge.md),
 * shared by Settings ("Ask Tilly to make changes") and the dashboard assistant
 * feed ("now what?"). Wraps ConciergeChatPanel in a modal, wired to the
 * business-scoped chat endpoint, and applies accepted changes via
 * applyConciergeChanges — the settings-safe apply path that only touches
 * fields the owner actually accepted, never the whole draft.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link as LinkIcon, Copy, Check } from 'lucide-react';
import { AppModal, AppButton, AppIconButton, useToast } from '@/components';
import { conciergeApi } from '@/api';
import { ConciergeChatPanel } from '@/pages/admin/onboarding/components/ConciergeChatPanel';
import { applyConciergeChanges } from '@/lib/concierge/applyConciergeChanges';
import type { OnboardingDraft } from '@/pages/admin/onboarding/types';
import { RequirePlan } from '@/auth/RequirePlan';
import { colors, spacing, radius } from '@/theme/tokens';

export interface AskTillyModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  title?: string;
  description?: string;
  placeholder?: string;
  quickReplies?: string[];
}

export function AskTillyModal({
  open,
  onClose,
  businessId,
  title = 'Ask Tilly',
  description = 'Tell Tilly what you want to change — services, hours, reminders — and review before anything is applied.',
  placeholder = 'e.g. Add a 90-minute balayage service for $180',
  quickReplies,
}: AskTillyModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [applying, setApplying] = useState(false);
  const [manageUrl, setManageUrl] = useState<string | undefined>(undefined);

  const handleConfirm = async (accepted: Partial<OnboardingDraft>) => {
    setApplying(true);
    try {
      const { ok, results } = await applyConciergeChanges(businessId, accepted);
      if (ok) {
        showSuccess('Changes applied.');
        queryClient.invalidateQueries({ queryKey: ['services', businessId] });
        queryClient.invalidateQueries({ queryKey: ['businessHours', businessId] });
        queryClient.invalidateQueries({ queryKey: ['reminderPolicy', businessId] });
        if (accepted.appointment || accepted.reschedule || accepted.cancellation) {
          queryClient.invalidateQueries({ queryKey: ['appointments'] });
        }
        const appointmentResult = results.find((r) => r.step === 'appointment' && r.manageUrl);
        if (appointmentResult?.manageUrl) {
          setManageUrl(appointmentResult.manageUrl);
          return;
        }
      } else {
        const failed = results.filter((r) => !r.ok).map((r) => r.step);
        showError(
          `Some changes couldn't be saved (${failed.join(', ')}). You can try again from Settings.`
        );
      }
      onClose();
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setManageUrl(undefined);
    onClose();
  };

  const [copied, setCopied] = useState(false);
  const handleCopyManageUrl = () => {
    if (!manageUrl) return;
    void navigator.clipboard.writeText(manageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (manageUrl) {
    return (
      <AppModal
        open={open}
        onClose={handleClose}
        title={t('admin:calendar.form.manageLinkTitle')}
        size="lg"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
          <p style={{ color: colors.textMuted, fontSize: '14px' }}>
            {t('admin:calendar.form.manageLinkDescription')}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
              padding: spacing[3],
              borderRadius: radius.md,
              backgroundColor: colors.surfaceRaised,
            }}
          >
            <LinkIcon size={16} style={{ color: colors.textMuted, flexShrink: 0 }} />
            <p
              style={{
                color: colors.textPrimary,
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {manageUrl}
            </p>
            <AppIconButton
              onClick={handleCopyManageUrl}
              aria-label={t('admin:calendar.form.copyLink')}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </AppIconButton>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: spacing[2] }}>
            <AppButton onClick={handleClose}>{t('admin:calendar.form.done')}</AppButton>
          </div>
        </div>
      </AppModal>
    );
  }

  return (
    <AppModal open={open} onClose={onClose} title={title} size="lg">
      <RequirePlan feature="aiConcierge" requiredTier="solo">
        <ConciergeChatPanel
          title={title}
          description={description}
          placeholder={placeholder}
          onSendTurn={(messages) => conciergeApi.chatTurn(businessId, { messages })}
          onSendTurnStream={(messages, handlers) =>
            conciergeApi.chatTurnStream(businessId, { messages }, handlers)
          }
          onConfirmDraft={handleConfirm}
          onDiscard={onClose}
          discardLabel={t('common:buttons.close')}
          confirming={applying}
          quickReplies={quickReplies}
          businessId={businessId}
        />
      </RequirePlan>
    </AppModal>
  );
}
